import { graphql } from "gatsby";
import React from "react";
import styled from "styled-components";
import parser from "html-react-parser";

import {
  IFixed,
  IFluid,
  ILocalFile
} from "src/types/local-files";
import { IIcons } from "src/components/icon/icon-list";

import { IImageMeta } from "../types/local-files";
import CollapseTags from "../components/collapse-tags/collapse-tags";
import { FullWidthColumnWrapper } from "../components/confines-wrappers";
import { IFieldTechnicalArticle } from "../components/field-technical-article/field-technical-article";
import Footer from "../components/footer";
import GetInTouch from "../components/get-in-touch";
import { Gradient } from "../components/gradients";
import Header from "../components/header";
import HelmetWrapper from "../components/helmet-wrapper";
import { breadCrumbSchema } from "../components/html-schema-snippets";
import itemList, {
  IItemListItem,
  IItemListSchema
} from "../components/html-schema-snippets/item-list";
import Layout from "../components/layout";
import PageTop from "../components/page-top";
import Tags from "../components/project-tags";
import ProjectsGrid from "../components/projects-grid";
import Text from "../components/text";
import TextPanel from "../components/text-panel";
import {
  colourHelper,
  fontLineHeightHelper,
  fontSizeHelper,
  fontWeightHelper
} from "../theme/theme-helper";
import { IProfileData } from "./about-us";

export interface IProjectData {
  title: string;
  id: string;
  created: string;
  changed: string;
  field_meta_description: string;
  field_project_client_name: string;
  field_cover_image: IImageMeta;
  field_project_cover_description: string;
  path: {
    alias: string;
  };
  relationships: {
    field_technical_article: IFieldTechnicalArticle[];
    field_project_tags?: IFieldProjectTag[];
    author: IProfileData;
    field_meta_image?: ILocalFile<IFixed>;
    field_cover_image: ILocalFile<IFluid>;
    field_hero_image: ILocalFile<IFixed>;
    field_mobile_hero_image: ILocalFile<IFixed>;
  };
}

export interface IFieldProjectTag {
  name: string;
  path: {
    alias: string;
  };
}

interface INodeData {
  node: IProjectData;
}

export interface ITaxonomyTermProjects {
  node: {
    name: string;
    path: {
      alias: string;
    };
  };
}

interface IProjectBoxOut {
  field_boxout_header: string;
  field_boxout_icon: keyof IIcons;
  field_boxout_text: {
    processed: string;
  };
  field_boxout_link?: {
    title: string;
    uri: string;
  };
}
interface IProjectProps {
  nodeProjectsOverview: {
    changed: string;
    title: string;
    field_hero_heading: string;
    field_meta_description: string;
    relationships: {
      field_projects_boxout_1: IProjectBoxOut;
      field_boxout_2: IProjectBoxOut;
      field_featured_projects: IProjectData[];
    };
  };
  allNodeProject: {
    edges: INodeData[];
  };
  allTaxonomyTermProjects: {
    edges: ITaxonomyTermProjects[];
  };
}

export const PageTopCustom = styled.div`
  .top-content {
    padding-top: 10%;
    padding-bottom: 10%;

    .heading {
      transition: 0.2s ease-in-out;
      display: block;
      margin-bottom: 0;
      color: ${({ theme }) => colourHelper({
    theme,
    variant: "periwinkle"
  })};
      font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "subhead_2"
    })};
      font-weight: ${({ theme }) =>
    fontWeightHelper({
      theme,
      variant: "heavy"
    })};
      line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "default"
    })};
    }

    .filtered-by {
      transition: 0.2s ease-in-out;
      display: block;
      padding-top: 3.2rem;
    }
  }

  @media (min-width: 480px) {
    .top-content {
      .filtered-by {
        padding-top: 4.6rem;
      }
    }
  }

  @media (min-width: 600px) {
    .top-content {
      .heading {
        display: flex;
        align-items: center;
        max-width: 52rem;
        min-height: 6.6rem;
      }

      .filtered-by {
        padding-top: 6.2rem;
      }
    }
  }

  @media (min-width: 768px) {
    .top-content {
      padding-top: 8.8rem;
      padding-bottom: 8.8rem;

      .heading {
        max-width: 62rem;
        min-height: 7.2rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "title"
    })};
        line-height: ${({ theme }) =>
    fontLineHeightHelper({
      theme,
      variant: "small"
    })};
      }
    }
  }

  @media (min-width: 1024px) {
    .top-content {
      .heading {
        max-width: 88rem;
        min-height: 10rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "display_2"
    })};
      }
    }
  }

  @media (min-width: 1280px) {
    .top-content {
      .heading {
        max-width: 120rem;
        min-height: 12.8rem;
        font-size: ${({ theme }) =>
    fontSizeHelper({
      theme,
      variant: "display_4"
    })};
      }
    }
  }

  @media (min-width: 1920px) {
    .top-content {
      .filtered-by {
        padding-top: 8.2rem;
      }
    }
  }
`;

export const ProjectsContainer = styled.div`
  .projects-grid-container {
    padding-top: 10%;
    padding-bottom: 10%;
  }

  @media (min-width: 600px) {
    .projects-grid-container {
      padding-top: 4%;
      padding-bottom: 4%;
    }
  }

  @media (min-width: 1440px) {
    .projects-grid-container {
      padding-top: 5.6rem;
      padding-bottom: 5.6rem;
    }
  }
`;

export const ProjectGridItem = styled.div`
  padding-top: 2rem;
  width: 100%;
  max-width: 36rem;

  &:first-child {
    padding-top: 0;
  }

  @media (min-width: 600px) {
    padding-top: 4%;
    padding-right: 2%;
    width: 50%;
    max-width: none;

    &:nth-child(2) {
      padding-top: 0;
    }

    &:nth-child(2n) {
      padding-left: 2%;
      padding-right: 0;
    }
  }
`;

/**
 * Projects page
 */
const ProjectsPage = ({ data }: { data: IProjectProps }) => {
  const boxOut1 =
    data.nodeProjectsOverview.relationships.field_projects_boxout_1;

  const boxOut2 = data.nodeProjectsOverview.relationships.field_boxout_2;

  const featuredProjects =
    data.nodeProjectsOverview.relationships.field_featured_projects;

  const breadcrumbsSchemaData = breadCrumbSchema([
    {
      name: "Projects",
      path: "/projects"
    }
  ]);

  let itemListSchemaData: IItemListSchema;
  let itemListElements: IItemListItem[] = [];
  let schemaJson = [];

  // Setup carousel schema
  itemListElements = featuredProjects.map((project, i) => {
    return {
      position: i + 1,
      path: project.path.alias
    };
  });
  // Generate the itemListSchemaData
  // eslint-disable-next-line prefer-const
  itemListSchemaData = itemList(itemListElements);
  // Set the schema JSON array
  schemaJson = [ breadcrumbsSchemaData, itemListSchemaData ];

  return (
    <Layout className="projects">
      <>
        <HelmetWrapper
          canonicalPath="/projects"
          title={data.nodeProjectsOverview.title}
          description={data.nodeProjectsOverview.field_meta_description}
          updatedAt={data.nodeProjectsOverview.changed}
        >
          <script type="application/ld+json">
            {JSON.stringify(schemaJson)}
          </script>
        </HelmetWrapper>

        <header
          role="banner"
          id="header"
        >
          <Header
            initialLogo
            initialTheme="dark"
          />

          <PageTopCustom id="hero">
            <PageTop
              gradient="darkBlueOrange"
              style={{ color: "white" }}
              height="small"
            >
              <Gradient
                gradient="purpleOrange"
                angle={75}
                options={{ colorStops: [ "80%", "100%" ] }}
                style={{
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  left: 0,
                  top: 0,
                  opacity: 0.5,
                  zIndex: -1
                }}
              />

              <FullWidthColumnWrapper className="top-content">
                <p className="heading">
                  {data.nodeProjectsOverview.field_hero_heading}
                </p>

                <Text
                  className="filtered-by"
                  fontSize="small_2"
                  letterSpacing="medium"
                  fontColor="third"
                  lineHeight="normalized"
                  style={{ textTransform: "uppercase" }}
                >
                  Filter by:
                </Text>

                <Tags data={data} />
              </FullWidthColumnWrapper>
            </PageTop>
          </PageTopCustom>
        </header>

        <CollapseTags
          root="/projects"
          rootTerm="Featured"
          tags={data.allTaxonomyTermProjects.edges}
        />

        <main
          id="main-content"
          role="main"
        >
          <ProjectsContainer>
            <h1 className="off-screen">
              {data.nodeProjectsOverview.title}
            </h1>

            <div className="projects-grid-container">
              <ProjectsGrid projects={featuredProjects.slice(0, 6)} />
            </div>

            <aside>
              <TextPanel
                label={boxOut1.field_boxout_header}
                text={parser(boxOut1.field_boxout_text.processed)}
                icon={boxOut1.field_boxout_icon}
                linkText={boxOut1.field_boxout_link ? boxOut1.field_boxout_link.title : undefined}
                linkUrl={boxOut1.field_boxout_link ? boxOut1.field_boxout_link.uri : undefined}
              />
            </aside>

            <div className="projects-grid-container">
              <ProjectsGrid projects={featuredProjects.slice(6, 10)} />
            </div>

            <aside>
              <TextPanel
                label={boxOut2.field_boxout_header}
                text={parser(boxOut2.field_boxout_text.processed)}
                icon={boxOut2.field_boxout_icon}
                linkText={boxOut2.field_boxout_link ? boxOut2.field_boxout_link.title : undefined}
                linkUrl={boxOut2.field_boxout_link ? boxOut2.field_boxout_link.uri : undefined}
              />
            </aside>

            <div className="projects-grid-container">
              <ProjectsGrid projects={featuredProjects.slice(10, 16)} />
            </div>
          </ProjectsContainer>
        </main>

        <footer role="contentinfo">
          <GetInTouch />

          <Footer />
        </footer>
      </>
    </Layout>
  );
};

export const query = graphql`
  query projectQuery($projectsLimit: Int = 10, $tagLimit: Int = 9) {
    nodeProjectsOverview {
      created
      changed
      title
      field_hero_heading
      field_meta_description
      relationships {
        field_featured_projects {
          ...getProjectData
        }
        field_projects_boxout_1 {
          field_boxout_header
          field_boxout_icon
          field_boxout_text {
            processed
          }
          field_boxout_link {
            title
            uri
          }
        }
        field_boxout_2 {
          field_boxout_header
          field_boxout_icon
          field_boxout_text {
            processed
          }
          field_boxout_link {
            title
            uri
          }
        }
      }
    }
    ...getAllProjects
    ...getProjectTags
  }
`;

export default ProjectsPage;
